import React, { useEffect, useState } from 'react';
import {
  Alert,
  Card,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { Link } from 'react-router-dom';

import { fetchAssessmentContent } from '../Assessments/api';
import {
  EllipsisText,
  formatDateTime,
  formatScore,
} from '../Assessments/shared';
import { AssessmentContent } from '../Assessments/types';
import { ValiditySummary } from './actionTypes';
import { readableError } from './errors';
import { JobsStrip } from './Jobs';
import { BenchmarkRollupRow } from './types';

/**
 * What an answer key looks like inside, and the two sentences other screens
 * say about a set of them.
 *
 * This module used to be the fleet list of answer keys, with its own
 * "Generate answer key" action, a versions drawer and a publish button. The
 * blueprint page is the front door now: an answer key is derived from the
 * blueprint page, audited there and published there, so the list, the
 * drawer and the action are gone. What stays is what that page reuses --
 * the content drilldown, the fleet sentence and the "not here" notice.
 */

/**
 * The rollup row as the read endpoint serves it: the quality-audit verdict
 * travels on the answer key, so a screen listing keys can say how many of
 * them are failing without a second read.
 */
export type RollupRow = BenchmarkRollupRow & {
  validity?: ValiditySummary | null;
};

/**
 * Diverging PCS/ICS cell shade: green for agreement, red for disagreement,
 * intensity by magnitude. Inline because the palette is data-driven, not a
 * fixed class; the text stays default-colored for legibility.
 */
const scoreCellStyle = (
  value: number | null | undefined,
): React.CSSProperties => {
  if (value == null) return {};
  const bounded = Math.max(-1, Math.min(1, Number(value)));
  const alpha = Math.min(0.85, Math.abs(bounded) * 0.7 + 0.08);
  return {
    background:
      bounded >= 0
        ? `rgba(82, 196, 26, ${alpha})`
        : `rgba(255, 77, 79, ${alpha})`,
    textAlign: 'center',
  };
};

/**
 * The drilldown reuses the EXISTING assessment content endpoint — the same
 * data the Assessments admin edits — so this screen adds no answer-key
 * endpoint of its own. PCS heatmap: rows periods, columns instrument codes.
 * ICS: the per-news scores nested per period, on the same columns.
 *
 * Mounted by code. The answer key's id is optional: with it, the recent
 * jobs on the key ride along; without it (a drawer that only knows the code)
 * the drilldown says nothing about jobs rather than guessing.
 */
export const BenchmarkDrilldown: React.FC<{
  code: string;
  scenarioId?: number;
}> = ({ code, scenarioId }) => {
  const [content, setContent] = useState<AssessmentContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchAssessmentContent(code)
      .then(result => {
        if (!cancelled) setContent(result);
      })
      .catch(requestError => {
        if (!cancelled)
          setError(
            readableError(requestError, { what: "This answer key's content" }),
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (loading) {
    return (
      <div className="assessment-mapping-detail-table">
        <Spin tip="Loading this answer key" />
      </div>
    );
  }
  if (error || !content) {
    return (
      <Alert
        className="assessment-mapping-detail-table"
        type="error"
        showIcon
        title={error || 'This answer key could not be loaded.'}
      />
    );
  }

  const instrumentCodes = Array.from(
    new Set([
      ...content.instruments.map(row => row.code),
      ...content.benchmarks.map(row => row.instrument_code),
    ]),
  );

  const pcsByPeriod: Record<number, Record<string, number | null>> = {};
  content.benchmarks.forEach(row => {
    (pcsByPeriod[row.period_index] ||= {})[row.instrument_code] = row.pcs;
  });

  const periods = [...content.periods].sort((a, b) => a.index - b.index);

  const pcsColumns: any[] = [
    {
      title: 'Period',
      fixed: 'left',
      width: 190,
      render: (_: unknown, period: (typeof periods)[number]) => (
        <Space direction="vertical" size={0}>
          <span>
            <Typography.Text code>{period.label}</Typography.Text>{' '}
            {period.regime_label && <Tag>{period.regime_label}</Tag>}
          </span>
          <Typography.Text type="secondary">
            {period.start_date} – {period.end_date}
          </Typography.Text>
        </Space>
      ),
    },
    ...instrumentCodes.map(instrument => ({
      title: <EllipsisText code value={instrument} />,
      width: 90,
      onCell: (period: (typeof periods)[number]) => ({
        style: scoreCellStyle(pcsByPeriod[period.index]?.[instrument]),
      }),
      render: (_: unknown, period: (typeof periods)[number]) =>
        formatScore(pcsByPeriod[period.index]?.[instrument]),
    })),
  ];

  const newsColumns: any[] = [
    {
      title: 'Period',
      fixed: 'left',
      width: 90,
      dataIndex: 'period_label',
      render: (value: string) => (
        <Typography.Text code>{value}</Typography.Text>
      ),
    },
    {
      title: 'Headline',
      fixed: 'left',
      width: 320,
      render: (_: unknown, row: AssessmentContent['news_scores'][number]) => (
        <Tooltip title={row.headline} placement="topLeft">
          <span className="assessment-ellipsis">{row.headline}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      width: 160,
      className: 'assessment-cell-nowrap',
      render: (value: string | null) => formatDateTime(value),
    },
    ...instrumentCodes.map(instrument => {
      const icsValue = (row: AssessmentContent['news_scores'][number]) => {
        const raw = (row.ics || {})[instrument];
        return typeof raw === 'number' ? raw : null;
      };
      return {
        title: <EllipsisText code value={instrument} />,
        width: 90,
        onCell: (row: AssessmentContent['news_scores'][number]) => ({
          style: scoreCellStyle(icsValue(row)),
        }),
        render: (_: unknown, row: AssessmentContent['news_scores'][number]) =>
          formatScore(icsValue(row)),
      };
    }),
  ];

  const newsRows = [...content.news_scores].sort(
    (a, b) =>
      a.period_index - b.period_index ||
      String(a.timestamp).localeCompare(String(b.timestamp)),
  );

  return (
    <div className="assessment-mapping-detail-table assessment-detail-stack">
      {scenarioId != null && (
        <Card size="small" title="Recent jobs on this answer key">
          <JobsStrip target_kind="assessment_scenario" target_id={scenarioId} />
        </Card>
      )}
      <Card size="small" title="Consistency by period (PCS)">
        <Table
          rowKey="id"
          className="assessment-table"
          tableLayout="fixed"
          dataSource={periods}
          columns={pcsColumns}
          scroll={{ x: 190 + instrumentCodes.length * 90 }}
          pagination={false}
        />
      </Card>
      <Card size="small" title="Consistency by headline (ICS)">
        <Table
          rowKey="id"
          className="assessment-table"
          tableLayout="fixed"
          dataSource={newsRows}
          columns={newsColumns}
          scroll={{ x: 570 + instrumentCodes.length * 90 }}
          pagination={{ defaultPageSize: 25, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
};

/**
 * The one sentence a list of answer keys cannot say about itself.
 *
 * Thirteen ACTIVE rows sat beside thirteen red FAILs with nothing above them,
 * and a newcomer read that as either "the page is broken" or "ACTIVE means
 * fine". It is neither: those answer keys are grading people right now, and
 * the quality audit says they cannot tell skill from clicking. The sentence
 * counts what it is given rather than asserting a number somebody typed.
 */
export const fleetSentence = (rows: RollupRow[]): string | null => {
  const active = rows.filter(row => row.status_label === 'active');
  const failing = active.filter(
    row => String(row.validity?.verdict || '').toLowerCase() === 'fail',
  );
  if (!failing.length) return null;
  const unexcused = failing.filter(row => !row.validity?.overridden).length;
  const graded = failing.reduce(
    (total, row) => total + (row.session_count || 0),
    0,
  );
  return (
    `${failing.length} of ${active.length} live answer key` +
    `${active.length === 1 ? '' : 's'} fail the quality audit. ` +
    `${graded} session${graded === 1 ? ' has' : 's have'} already been graded ` +
    `by them. ` +
    (unexcused
      ? `${unexcused} of these ${unexcused === 1 ? 'has' : 'have'} no recorded override.`
      : 'Every one carries a written override.')
  );
};

/**
 * What a chip that landed nowhere is told to say.
 *
 * A screen built around the answer keys the scenario builder derived for a
 * blueprint family cannot show one that was authored by hand under Answer
 * keys, or whose family predates the tag. Those are real, gradeable and
 * simply not here. So the notice names the one that was asked for, says
 * where it lives instead, and links there.
 *
 * A chip that carried a CODE can be routed all the way, because the answer
 * keys workspace opens on a code. One that carried only a numeric id cannot:
 * nothing here can turn an id into a code, and a link built from an id would
 * land on "not found", which is a worse answer than an honest one. It goes to
 * the catalogue, where the id is beside every row.
 */
export const UnlistedNotice: React.FC<{
  asked: string;
  onClose?: () => void;
}> = ({ asked, onClose }) => {
  const isCode = !/^\d+$/.test(asked);
  const href = isCode
    ? `/dashboard/assessments?scenario=${encodeURIComponent(asked)}`
    : '/dashboard/assessments';
  return (
    <Alert
      className="assessment-callout"
      showIcon
      closable
      onClose={onClose}
      type="warning"
      data-testid="unlisted-answer-key"
      title={
        isCode
          ? `Answer key ${asked} is not here`
          : `Answer key #${asked} is not here`
      }
      description={
        <Space direction="vertical" size={4}>
          <span>
            This page holds the answer keys the scenario builder derived for a
            blueprint family it minted and tagged. This one is not one of them —
            it was authored by hand under Answer keys, or its blueprint family
            predates the tag. It still grades sessions; it is just managed
            there.
          </span>
          <Link to={href}>
            {isCode ? `Open ${asked} under Answer keys` : 'Open Answer keys'}
          </Link>
        </Space>
      }
    />
  );
};
